import { useCallback } from "react";
import { useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "src/business-hooks/useCurrentUser";
import type { MetricTypeName } from "src/business-utils/metrics/metricTypes";
import type { PipelineNotificationSettings } from "src/queries/pipelines/pipelineSettings.query";
import { dfetch, type FetchMethod } from "src/utils/dfetch.ts";
import { useModal } from "@components/ConfirmationModal/ConfirmationModal.tsx";
import { dismissNotification, notify } from "@utils/notification.ts";

export type MetricConfigPutObj = {
  metric_type: MetricTypeName;
  asset_name: string;
  asset_type: string;
  is_metric_enabled: boolean;
  task_name?: string;
};

export type MetricConfigPut = {
  metrics_conf: MetricConfigPutObj[];
  app_name: string;
  env_name: string;
};

export const useMutationEditMetrics = () => {
  return useCommonMutation<MetricConfigPut>({
    url: `/api/metrics_conf`,
    method: "PUT",
    successMessage: "Updated metrics",
    failureMessage: "Failed updating metrics",
  });
};

// (OHAD) - commented out because did not see usage anywhere and there's some ownership auth issue
// export const useMutationDisableMetrics = () => {
//   const queryClient = useQueryClient();
//   const { enqueueSnackbar } = useSnackbar();
//   return useMutation({
//     mutationFn: (metrics: number[]) => {
//       return dfetch("/api/app/metrics/config/disable", metrics, "POST");
//     },
//     onSuccess() {
//       void queryClient.invalidateQueries();
//       enqueueSnackbar("Disabled metrics", { variant: "success" });
//     },
//     onError() {
//       void queryClient.invalidateQueries();
//       enqueueSnackbar("Failed disabling metrics", { variant: "error" });
//     },
//   });
// };

export const useMutationEnableMetrics = () => {
  return useCommonMutation<{
    metric_id: number;
    is_metric_enabled: true;
    app_name: string;
    env_name: string;
  }>({
    url: "/api/metrics_conf",
    method: "PUT",
    successMessage: "Enabled metric",
    failureMessage: "Failed enabling metric",
  });
};

export const useCommonMutation = <Body>(options: {
  url: string | ((params: Body) => string);
  method: FetchMethod;
  successMessage: string;
  failureMessage: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: Body) => {
      const url = options.url instanceof Function ? options.url(body) : options.url;
      try {
        return await dfetch(url, body, options.method);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries();
      const key = notify(options.successMessage, {
        variant: "success",
        persist: false,
      });

      setTimeout(() => {
        dismissNotification(key);
      }, 3000);
    },
    onError: (error) => {
      void queryClient.invalidateQueries();
      const key = notify(options.failureMessage, {
        variant: "error",
        error,
        persist: false,
      });
      setTimeout(() => {
        dismissNotification(key);
      }, 3000);
    },
  });
};

export const useMutationUpdatePipelineOwners = () => {
  const { env_name, app_name } = useParams();

  return useCommonMutation<{
    users: (string | number)[];
  }>({
    url: `/api/envs/${env_name}/apps/${app_name}/owners`,
    method: "PUT",
    successMessage: "Updated pipeline owners",
    failureMessage: "Failed updating pipeline owners",
  });
};

export const useMutationUpdatePipelineTags = () => {
  const { env_name, app_name } = useParams();

  return useCommonMutation<{
    tags: (string | number)[];
  }>({
    url: `/api/envs/${env_name}/apps/${app_name}/tags`,
    method: "PUT",
    successMessage: "Updated pipeline tags",
    failureMessage: "Failed updating pipeline tags",
  });
};

export const useMutationPipelineSettings = () => {
  const { env_name, app_name } = useParams();

  return useCommonMutation<
    Partial<
      Omit<PipelineNotificationSettings, "alert_pipeline_emails_list"> & {
        alert_pipeline_emails_list: string[];
      }
    >
  >({
    url: `/api/envs/${env_name}/apps/${app_name}/settings`,
    method: "PUT",
    successMessage: "Updated notification settings",
    failureMessage: "Failed updating notification settings",
  });
};

export const useMutationTestNotificationChannels = () => {
  const { env_name, app_name } = useParams();

  return useCommonMutation<undefined>({
    url: `/api/envs/${env_name}/apps/${app_name}/settings/test`,
    method: "POST",
    successMessage: "Sent a notification settings test",
    failureMessage: "Failed sending a notification settings test",
  });
};

export const useMutationDisableMetric = () => {
  return useCommonMutation<{ metric_id: number }>({
    url: "/api/metrics_conf/disable",
    method: "POST",
    successMessage: "Disabled metrics successfully",
    failureMessage: "Failed disabling metrics",
  });
};

export const useMutationUpdateMetricSettings = (metricId: number) => {
  const { env_name, app_name } = useParams();

  return useCommonMutation({
    url: `/api/envs/${env_name}/apps/${app_name}/metrics/${metricId}`,
    method: "PUT",
    successMessage: "Updated metric",
    failureMessage: "Failed updating metric",
  });
};

export const useMutationUpdateAssetSettings = (assetName: string) => {
  return useCommonMutation({
    url: `/api/assets/${assetName}`,
    method: "PUT",
    successMessage: "Updated asset settings",
    failureMessage: "Failed updating asset settings",
  });
};

export const useMutationChangeCurrentUserPipelineOwnership = () => {
  const mutation = useCommonMutation<{
    env_name: string;
    app_name: string;
    addOwner?: number;
    removeOwner?: number;
  }>({
    url: ({ env_name, app_name }) => `/api/envs/${env_name}/apps/${app_name}/owners`,
    method: "PUT",
    successMessage: "Updated pipeline ownership",
    failureMessage: "Failed updating pipeline ownership",
  });
  const modal = useModal();
  const currentUser = useCurrentUser();

  return useCallback(
    async ({
      env_name,
      app_name,
      action,
    }: {
      env_name: string;
      app_name: string;
      action: "add" | "remove";
    }) => {
      if (!currentUser?.user_id) {
        throw new Error("Current user not found");
      }

      await modal.confirm({
        title: "Are You Sure?",
        message:
          "By removing yourself as an owner, you'll lose access to ownership privileges",
        agree: "Make me an owner",
      });
      if (action === "add") {
        return mutation.mutate({
          env_name,
          app_name,
          addOwner: currentUser.user_id,
        });
      } else if (action === "remove") {
        return mutation.mutate({
          env_name,
          app_name,
          removeOwner: currentUser.user_id,
        });
      }
    },
    [currentUser?.user_id, modal, mutation]
  );
};
